import { useState, useRef, useEffect } from 'react'

import { CardContainer, SavingsCalcImage, CtaContainer, ValueContainer, TotalSavingsValueContainer } from './Styles'
import ButtonLink, { ButtonLinkProps } from '../../core/ButtonLink/ButtonLink'
import { FlexGrid, Typography, Card, Divider, Spacer, ChevronLink } from '@telus-uds/components-web'
import { BlockRenderer } from '@/siteBuilder/renderer/contentful/BlockRenderer'
import { LinkProps } from '../../core/Link/Link'
import { LegalTextRenderer } from '@/siteBuilder/utils/text/TextRenderer'
import { HeadingProps } from '../../core/Heading/Heading'
import { ImageProps } from '../../core/Image/Image'

type SavingsCardProps = {
  savingsHeader: { fields: HeadingProps }
  savingsRateText: string
  homeValueLabel: string
  homeImage: ImageProps
  deviceValueLabel: string
  restartButtonText: string
  rewardsValueLabel?: string
  rewardLink?: LinkProps
  deviceSavings: number
  rewardsSavings?: number
  homeSavings: number
  isMobile: boolean
  deviceSavingsCardRef: React.RefObject<HTMLDivElement>
  setShowSavingsCard: (val: boolean) => void
  isTablet: boolean
  step3CTAProps: ButtonLinkProps
  step3SubHeading: string
  totalSavingsValue: string
  showSavingsCard: boolean
}

const SavingsCard = ({
  savingsHeader,
  savingsRateText,
  homeValueLabel,
  homeSavings,
  homeImage,
  deviceValueLabel,
  deviceSavings,
  restartButtonText,
  rewardsValueLabel,
  rewardsSavings,
  rewardLink,
  isMobile,
  deviceSavingsCardRef,
  setShowSavingsCard,
  isTablet,
  step3CTAProps,
  step3SubHeading,
  totalSavingsValue,
  showSavingsCard,
}: SavingsCardProps) => {
  const savingsCardRef = useRef<HTMLDivElement>(null)
  const [savingsImageHeight, setSavingsImageHeight] = useState<number>(0)

  useEffect(() => {
    const calculateHeight = () => {
      if (savingsCardRef.current) {
        setSavingsImageHeight(savingsCardRef.current.clientHeight)
      }
    }

    calculateHeight()

    const observer = new ResizeObserver(calculateHeight)
    if (savingsCardRef.current) {
      observer.observe(savingsCardRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [showSavingsCard])

  const renderValue = (label: string, savings: number, block?: SavingsCardProps['rewardLink']) => {
    return (
      <>
        <FlexGrid.Col xs={6} md={3}>
          <ValueContainer data-testid={`${label}-value`} isMobile={isMobile}>
            <Typography block variant={{ size: 'h5' }}>
              {label}
            </Typography>
            <Typography variant={{ size: 'h2', colour: 'light', bold: true }}>
              {'$' + savings.toFixed(0).toString()}
            </Typography>
          </ValueContainer>
          {block && <BlockRenderer block={block} />}
        </FlexGrid.Col>
      </>
    )
  }
  const savingsHeaderAdjusted = {
    ...savingsHeader,
    fields: { ...savingsHeader.fields, size: 'H1', bold: true, colour: 'default' },
  }

  const step3CTAPropsAdjusted: ButtonLinkProps = { ...step3CTAProps, width: 'responsive', priority: 'high' }

  const totalSavingsBlock = (
    <>
      <BlockRenderer block={savingsHeaderAdjusted} />
      <Spacer space={{ xs: 3, md: 4 }} />
      <TotalSavingsValueContainer>
        <Typography variant={{ size: isMobile ? 'display2' : 'display1', bold: true }}>
          {'$' + totalSavingsValue}
        </Typography>
        <Typography
          variant={{ size: 'display2', bold: isTablet ? true : false, colour: 'brand' }}
          {...(!isTablet && { tokens: { fontSize: '24px', fontWeight: '500' } })}
        >
          <LegalTextRenderer>{savingsRateText}</LegalTextRenderer>
        </Typography>
      </TotalSavingsValueContainer>
      <Spacer space={{ xs: 2 }} />
      <div>
        <Typography variant={{ size: 'h4' }} tokens={{ fontWeight: '400' }}>
          {step3SubHeading}
        </Typography>
      </div>
      <Spacer space={{ xs: 4, md: 5 }} />
    </>
  )

  const renderBackButton = () => {
    return (
      <ChevronLink
        direction="left"
        onPress={() => {
          setShowSavingsCard(false)
          deviceSavingsCardRef.current?.scrollIntoView({ behavior: 'smooth' })
        }}
      >
        {restartButtonText}
      </ChevronLink>
    )
  }

  return (
    <FlexGrid.Row ref={deviceSavingsCardRef}>
      <FlexGrid.Col xs={12} lg={8}>
        <Card ref={savingsCardRef} tokens={{ borderWidth: 0 }} variant={{ padding: 'custom' }}>
          <CardContainer isMobile={!!isMobile}>
            {totalSavingsBlock}
            <Divider variant={{ weight: 'thick', decorative: true }} />
            <FlexGrid gutter={false}>
              <FlexGrid.Row>
                {renderValue(homeValueLabel, homeSavings)}
                {renderValue(deviceValueLabel, deviceSavings)}
                {rewardsSavings != null &&
                  rewardsSavings !== 0 &&
                  renderValue(rewardsValueLabel, rewardsSavings, rewardLink)}
              </FlexGrid.Row>
            </FlexGrid>
            {isMobile ? (
              <>
                <Spacer space={5} />
                {renderBackButton()}
                <Spacer space={4} />
                <ButtonLink {...step3CTAPropsAdjusted} />
              </>
            ) : (
              <>
                <Spacer space={7} />
                <CtaContainer justifyContent="space-between">
                  <CtaContainer>{renderBackButton()}</CtaContainer>
                  <CtaContainer>
                    <ButtonLink {...step3CTAPropsAdjusted} />
                  </CtaContainer>
                </CtaContainer>
              </>
            )}
          </CardContainer>
        </Card>
      </FlexGrid.Col>
      <FlexGrid.Col xs={0} lg={4}>
        <SavingsCalcImage height={savingsImageHeight}>
          <BlockRenderer block={homeImage} />
        </SavingsCalcImage>
      </FlexGrid.Col>
    </FlexGrid.Row>
  )
}

export default SavingsCard
